import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Checkbox, DatePicker, Spin } from 'antd';
import SubPageLayout from '../helpers/SubPageLayout';
import FormSection from '../helpers/FormSection';
import FormField, { FormFieldOverrideOnChange, InputFiledWithNone } from '../helpers/FormField';
import {
  AirFilterTypesDropdown,
  BuildLocationDropdown,
  ChamberTypesDropdown,
  ChemicalTypesDropdown,
  CompanySelectDropDown,
  GasMonitorTypesDropdown,
  GasSensorTypesDropdown,
  ExternalNodeGasSensorTypesDropdown,
  InteriorLightingTypesDropdown,
  OptionsTypesDropdown,
  ScrubberTypesDropdown,
  SiteSelectDropdown,
  CommonDropdown,
} from '../helpers/drop-downs-for-enums';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewChamber,
  addNewChamberLoading,
  ChambersActions,
  clearChamberEditing,
  getChamberById,
  getEditingChamber,
  updateChamber,
} from '../../../features/chamber.store';
import { getUser } from '../../../features/auth.store';
import to from '../../../utils/to';
import { toast } from 'react-toastify';
import eng from '../../../utils/eng';
import moment from 'moment';
import { map, range } from 'lodash';

const getTenantIdentifierFromUser = (user) =>
  user?.tenant?.identifier ||
  user?.tenant?.id ||
  user?.tenantIdentifier ||
  user?.tenant_identifier ||
  user?.tenantId ||
  '';

const extractApiErrorMessages = (err) => {
  const data = err?.data;
  const messages = [];
  const detailMessages = [];

  const pushMessage = (value) => {
    if (!value) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    messages.push(normalized);
  };

  pushMessage(err?.message);
  pushMessage(err?.err);
  pushMessage(data?.message);
  pushMessage(data?.error);
  pushMessage(data?.detail);
  pushMessage(data?.title);

  if (Array.isArray(data?.details)) {
    data.details.forEach((item) => {
      const normalized = String(item || '').trim();
      if (normalized) detailMessages.push(normalized);
    });
  }

  const collectStringLeaves = (value) => {
    if (value === null || value === undefined) return;

    if (typeof value === 'string') {
      pushMessage(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => collectStringLeaves(item));
      return;
    }

    if (typeof value === 'object') {
      Object.values(value).forEach((entry) => collectStringLeaves(entry));
    }
  };

  const nestedCollections = [
    data?.errors,
    data?.fieldErrors,
    data?.violations,
    data?.subErrors,
  ];

  nestedCollections.forEach((collection) => {
    if (Array.isArray(collection)) {
      collection.forEach((item) => {
        if (typeof item === 'string') {
          pushMessage(item);
          return;
        }

        pushMessage(item?.message);
        pushMessage(item?.defaultMessage);
        pushMessage(item?.error);
        pushMessage(item?.detail);
      });
      return;
    }

    if (collection && typeof collection === 'object') {
      Object.values(collection).forEach((value) => {
        if (Array.isArray(value)) {
          value.forEach((entry) => pushMessage(entry));
        } else {
          collectStringLeaves(value);
        }
      });
    }
  });

  collectStringLeaves(data);

  const uniqueDetailMessages = [...new Set(detailMessages)].filter(Boolean);
  if (uniqueDetailMessages.length) {
    return uniqueDetailMessages;
  }

  const genericMessages = new Set(['internal server error', 'request failed']);
  const uniqueMessages = [...new Set(messages)].filter(Boolean);
  const specificMessages = uniqueMessages.filter((m) => !genericMessages.has(String(m).toLowerCase()));

  if (specificMessages.length) {
    return specificMessages;
  }

  if (err?.status) {
    return [`${err?.message || eng.somethingWentWrong} (HTTP ${err.status})`];
  }

  return uniqueMessages.length ? uniqueMessages : [eng.somethingWentWrong];
};

const showApiErrors = (err) => {
  const allMessages = extractApiErrorMessages(err);
  allMessages.forEach((message, index) => {
    toast.error(message, { toastId: `chamber-error-${index}-${message}` });
  });
};

const FormikCheckboxField = ({ name, children }) => {
  const [field, , helpers] = useField({ name, type: 'checkbox' });

  return (
    <div style={{ marginBottom: 16 }}>
      <Checkbox
        checked={!!field.value}
        onChange={(e) => helpers.setValue(e.target.checked)}
      >
        {children}
      </Checkbox>
    </div>
  );
};

const ChambersAdd = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const history = useHistory();

  const [showForm, setShowForm] = useState(false);

  const user = useSelector(getUser);
  const editingChamber = useSelector(getEditingChamber);
  const addNewLoading = useSelector(addNewChamberLoading);

  const userTenantIdentifier = useMemo(() => getTenantIdentifierFromUser(user), [user]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (id) {
        dispatch(clearChamberEditing());
        await dispatch(getChamberById({ id }));
      }
      if (mounted) setShowForm(true);
    })();

    return () => {
      mounted = false;
    };
  }, [id, dispatch]);



  const initialValues = useMemo(() => {
  if (id && editingChamber) {
    return {
      ...initialValue,
      ...editingChamber,
      chamberID: editingChamber?.chamberID ?? editingChamber?.name ?? '',
      name: editingChamber?.name ?? '',
      expectedDelivery: editingChamber?.expectedDelivery
        ? moment(editingChamber.expectedDelivery)
        : null,
      tenantIdentifier:
        editingChamber?.tenantIdentifier ||
        editingChamber?.tenant?.identifier ||
        '',
      siteId:
        editingChamber?.siteId !== null && editingChamber?.siteId !== undefined
          ? String(editingChamber.siteId)
          : editingChamber?.site?.id !== null && editingChamber?.site?.id !== undefined
          ? String(editingChamber.site.id)
          : null,
      gasSensorTypes: editingChamber?.gasSensorTypes || [],
      externalNodeGasSensorTypes: editingChamber?.externalNodeGasSensorTypes || [],
      optionsTypes: editingChamber?.optionsTypes || [],
    };
  }

  return {
    ...initialValue,
    tenantIdentifier: userTenantIdentifier || null,
  };
}, [id, editingChamber, userTenantIdentifier]);



  const onSave = async ({ expectedDelivery, siteId, ...values }) => {
        const normalizedChamberID = values?.chamberID?.trim();
        const normalizedName = values?.name?.trim();

        const payload = {
          ...values,
          chamberID: normalizedChamberID || normalizedName || '',
          expectedDelivery: expectedDelivery?.toDate
            ? expectedDelivery.toDate().getTime()
            : expectedDelivery || null,
          siteId: siteId !== null && siteId !== undefined && siteId !== '' ? Number(siteId) : null,
        };

        //console.log('Payload being sent to API:', payload);

        if (id) {
          const [err] = await to(dispatch(updateChamber({ values: payload, id })).unwrap());
          if (err) {
            showApiErrors(err);
            return;
          }
          toast.success(eng.toast.updateChamberSuccessfully);
        } else {
          const [err] = await to(dispatch(addNewChamber({ values: payload })).unwrap());
          if (err) {
            showApiErrors(err);
            return;
          }
          toast.success(eng.toast.addChamberSuccessfully);
        }

        history.push('/chambers');
      };

  const getServiceMonthsList = () =>
    map(range(1, 13), (month) => ({ label: `${month}`, value: `${month}` }));

  return (
    <SubPageLayout title={id ? 'Edit Chamber' : 'Add Chambers'} loading={addNewLoading}>
      {showForm ? (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSave}
          validateOnBlur
          validationSchema={YupValidation}
        >
          {({ values, setFieldValue, submitCount, errors }) => (
            <Form>
              <FormSection title="Site Details" className="space-bottom">
                <div className="d-flex">
                  <div className="flex space-right">
                    <FormField
                      name="tenantIdentifier"
                      labelText="Company"
                      placeholder="Input Here"
                      InputComp={CompanySelectDropDown}
                      extraOnChange={(val) => {
                        setFieldValue('tenantIdentifier', val);
                        setFieldValue('siteId', null);
                        dispatch(ChambersActions.setSelectedCompany(val));
                        dispatch(ChambersActions.setSelectedSite(null));
                      }}
                    />

                    <FormField
                      name="siteId"
                      labelText="Site"
                      placeholder="Input Here"
                      company={values.tenantIdentifier || ''}
                      value={values.siteId}
                      extraOnChange={(val) => {
                        setFieldValue('siteId', val);
                        dispatch(ChambersActions.setSelectedSite(val));
                      }}
                      InputComp={SiteSelectDropdown}
                    />

                    <FormField
                      name="chamberID"
                      labelText="Chamber ID"
                      placeholder="Input Here"
                    />

                    <FormField
                      name="name"
                      labelText="Chamber Name"
                      placeholder="Input here"
                    />
                  </div>

                  <div className="flex space-left">
                    <FormField
                      name="buildLocation"
                      labelText="Build Location"
                      placeholder="Input Here"
                      InputComp={BuildLocationDropdown}
                    />

                    <FormFieldOverrideOnChange
                      name="expectedDelivery"
                      labelText="Expected Delivery"
                      InputComp={(props) => (
                        <DatePicker
                          {...props}
                          className="w-100"
                          value={props.value ? moment(props.value) : null}
                        />
                      )}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection title="Chamber Details">
                <div className="d-flex">
                  <div className="flex space-right">
                    <FormField
                      name="chamberType"
                      labelText="Chamber Type"
                      placeholder="Input Here"
                      InputComp={ChamberTypesDropdown}
                    />

                    <FormField
                      name="scrubberType"
                      labelText="Scrubber Type"
                      placeholder="Input Here"
                      InputComp={ScrubberTypesDropdown}
                      excludeValues={['Multi-purpose']}
                    />

                    <FormField
                      name="chemicalType"
                      labelText="Chemical Type"
                      placeholder="Input Here"
                      InputComp={ChemicalTypesDropdown}
                    />

                    <FormField
                      name="airFilterType"
                      labelText="Compressed air filtration"
                      placeholder="Input Here"
                      InputComp={AirFilterTypesDropdown}
                    />

                    <FormField
                      name="gasMonitorType"
                      labelText="Digital gas monitoring"
                      placeholder="Input Here"
                      InputComp={GasMonitorTypesDropdown}
                    />

                    <FormFieldOverrideOnChange
                      mode="multiple"
                      allowClear
                      name="gasSensorTypes"
                      labelText="Gas sensors"
                      placeholder="Input Here"
                      InputComp={GasSensorTypesDropdown}
                    />

                    <FormFieldOverrideOnChange
                      mode="multiple"
                      allowClear
                      name="externalNodeGasSensorTypes"
                      labelText="External Node Gas sensors"
                      placeholder="Input Here"
                      InputComp={ExternalNodeGasSensorTypesDropdown}
                    />

                    <FormField
                      name="interiorLightingType"
                      labelText="Interior Lighting"
                      placeholder="Input Here"
                      InputComp={InteriorLightingTypesDropdown}
                    />

                    <FormFieldOverrideOnChange
                      mode="multiple"
                      allowClear
                      name="optionsTypes"
                      labelText="Options"
                      placeholder="Input Here"
                      InputComp={OptionsTypesDropdown}
                    />

                    <InputFiledWithNone
                      name="coTubeQuantity"
                      labelText="CO tube quantity"
                      placeholder="CO tube quantity"
                    />

                    <InputFiledWithNone
                      name="co2TubeQuantity"
                      labelText="CO2 tube quantity"
                      placeholder="CO2 tube quantity"
                    />

                    <InputFiledWithNone
                      name="o2TubeQuantity"
                      labelText="O2 tube quantity"
                      placeholder="O2 tube quantity"
                    />

                    <InputFiledWithNone
                      name="batteryQuantity"
                      labelText="Battery quantity"
                      placeholder="Battery quantity"
                    />

                    <FormField
                      name="additionalLanguages"
                      labelText="Additional Language"
                      placeholder="Additional Language"
                    />

                    <FormField
                      name="rspKitNo"
                      labelText="RSP Kit Number"
                      placeholder="RSP Kit Number"
                    />

                    <FormField
                      name="waterRequired"
                      labelText="Water Required"
                      placeholder="Water Required"
                    />

                    <FormField
                      name="serviceFrequency"
                      labelText="Service Frequency (Months)"
                      placeholder="Select Months"
                      options={getServiceMonthsList()}
                      InputComp={CommonDropdown}
                    />
                  </div>

                  <div className="flex space-right">
                    <FormField
                      name="capacity"
                      labelText="Capacity"
                      placeholder="capacity"
                    />

                    <FormField
                      name="duration"
                      labelText="Duration"
                      placeholder="duration"
                    />

                    <InputFiledWithNone
                      name="coChemicalQuantity"
                      labelText="CO chemical quantity"
                      placeholder="CO"
                    />

                    <InputFiledWithNone
                      name="co2ChemicalQuantity"
                      labelText="CO2 chemical quantity"
                      placeholder="CO2"
                    />

                    <InputFiledWithNone
                      name="moistureRemovalQuantity"
                      labelText="Moisture removal quantity"
                      placeholder="Moisture removal quantity"
                    />

                    <InputFiledWithNone
                      name="mainChamberOxygenLevel"
                      labelText="Medical oxygen cylinders (main chamber) quantity"
                      placeholder="Medical oxygen cylinders (main chamber) quantity"
                    />

                    <InputFiledWithNone
                      name="ppmsOxygenLevel"
                      labelText="Medical oxygen cylinders (PPMS) quantity"
                      placeholder="Medical oxygen cylinders (PPMS) quantity"
                    />

                    <InputFiledWithNone
                      name="airlockOxygenLevel"
                      labelText="Compressed Air cylinders (Airlock) quantity"
                      placeholder="Compressed Air cylinders (Airlock) quantity"
                    />

                    <FormikCheckboxField name="oxygenCandle">
                      Oxygen candle
                    </FormikCheckboxField>

                    <FormikCheckboxField name="manualGasTubes">
                      Manual gas tubes
                    </FormikCheckboxField>

                    <FormikCheckboxField name="externalFireExtinguisher">
                      Fire extinguisher - external
                    </FormikCheckboxField>

                    <FormikCheckboxField name="internalFireExtinguisher">
                      Fire extinguisher - internal
                    </FormikCheckboxField>

                    <FormikCheckboxField name="fireBlanket">
                      Fire blanket - internal
                    </FormikCheckboxField>

                    <FormikCheckboxField name="transformer">
                      Transformer
                    </FormikCheckboxField>

                    <FormikCheckboxField name="communicationsBox">
                      Communications box
                    </FormikCheckboxField>

                    <FormikCheckboxField name="toilet">
                      Toilet
                    </FormikCheckboxField>

                    <FormikCheckboxField name="longLifeFood">
                      Long-life food
                    </FormikCheckboxField>
                  </div>
                </div>
              </FormSection>

              {submitCount > 0 && Object.keys(errors).length > 0 && (
                <div style={{ color: '#ff4d4f', marginBottom: 16 }}>
                  Please fix the highlighted validation errors before submitting.
                </div>
              )}

              <div className="d-flex jc-center space-top-p">
                <Button type="primary" htmlType="submit" loading={addNewLoading}>
                  {id ? 'Update' : 'Submit'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <Spin spinning>
          <div style={{ width: '100%', height: '200px' }} />
        </Spin>
      )}
    </SubPageLayout>
  );
};

const initialValue = {
  tenantIdentifier: null,
  siteId: null,
  chamberID: '',
  name: '',
  buildLocation: null,
  expectedDelivery: null,
  chamberType: null,
  scrubberType: null,
  chemicalType: null,
  airFilterType: null,
  gasMonitorType: null,
  gasSensorTypes: [],
  externalNodeGasSensorTypes: [],
  interiorLightingType: null,
  optionsTypes: [],
  coTubeQuantity: '',
  co2TubeQuantity: '',
  o2TubeQuantity: '',
  batteryQuantity: '',
  additionalLanguages: '',
  rspKitNo: '',
  waterRequired: '',
  serviceFrequency: null,
  capacity: '',
  duration: '',
  coChemicalQuantity: '',
  co2ChemicalQuantity: '',
  moistureRemovalQuantity: '',
  mainChamberOxygenLevel: '',
  ppmsOxygenLevel: '',
  airlockOxygenLevel: '',
  oxygenCandle: false,
  manualGasTubes: false,
  externalFireExtinguisher: false,
  internalFireExtinguisher: false,
  fireBlanket: false,
  transformer: false,
  communicationsBox: false,
  toilet: false,
  longLifeFood: false,
};

const numberWithNon = Yup.string().test(
  'NoneOrNumber',
  'Must be None or Valid Number',
  (val) => val === undefined || val === null || val === '' || !isNaN(val) || val === 'None',
);



const YupValidation = Yup.object({
  tenantIdentifier: Yup.string().required('Required').nullable(),
  siteId: Yup.mixed().required('Required').nullable(),
  chamberID: Yup.string().transform((value, originalValue) => {
    return originalValue == null ? '' : value;
  }),
  name: Yup.string().required('Required'),
  buildLocation: Yup.string().required('Required').nullable(),
  expectedDelivery: Yup.mixed().required('Required').nullable(),
  chamberType: Yup.string().required('Required').nullable(),
  scrubberType: Yup.string().required('Required').nullable(),
  chemicalType: Yup.string().required('Required').nullable(),
  airFilterType: Yup.string().required('Required').nullable(),
  gasMonitorType: Yup.string().required('Required').nullable(),
  gasSensorTypes: Yup.array().of(Yup.string()).max(10, 'Select only 10'),
  externalNodeGasSensorTypes: Yup.array().of(Yup.string()).max(10, 'Select only 10'),
  interiorLightingType: Yup.string().required('Required').nullable(),
  optionsTypes: Yup.array().of(Yup.string()).min(1, 'Select at least 1'),
  coTubeQuantity: Yup.string().required('Required'),
  co2TubeQuantity: Yup.string().required('Required'),
  o2TubeQuantity: Yup.string().required('Required'),
  batteryQuantity: numberWithNon,
  additionalLanguages: Yup.string(),
  serviceFrequency: Yup.string().nullable(),
  capacity: numberWithNon,
  duration: numberWithNon,
  coChemicalQuantity: numberWithNon,
  co2ChemicalQuantity: numberWithNon,
  moistureRemovalQuantity: numberWithNon,
  mainChamberOxygenLevel: numberWithNon,
  ppmsOxygenLevel: numberWithNon,
  airlockOxygenLevel: numberWithNon,
  oxygenCandle: Yup.boolean(),
  manualGasTubes: Yup.boolean(),
  externalFireExtinguisher: Yup.boolean(),
  internalFireExtinguisher: Yup.boolean(),
  fireBlanket: Yup.boolean(),
  transformer: Yup.boolean(),
  communicationsBox: Yup.boolean(),
  toilet: Yup.boolean(),
  longLifeFood: Yup.boolean(),
  rspKitNo: Yup.string().required('Required'),
  waterRequired: Yup.string(),
});

export default ChambersAdd;